import { useState, useMemo } from "react";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import RefreshIcon from '@mui/icons-material/Refresh';

import { buildChargeAccountDataSource, getPreviousExecutableIdx, resolveSocialSecurityPayment } from "../../functions";
import { HandlePdfChargeAccount, updateContract } from "../../services/backend/ContractService";

const ContractViewActions = ({
  contract,
  setContract,
  payment,
  paymentKey,
  idx,
  user,
  users,
  getContractFieldWithNews,
  downloadChargeAccount,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const hasChargeAccount = !!payment?.chargeAccount;
  const hasChargeAccountFull = !!payment?.chargeAccountFull;
  const isAdmin = !!user?.roles?.includes('ADMIN');

  // Periodo ejecutable anterior: salta los meses que una suspensión dejó en
  // cero. -1 => este es el primer periodo del contrato.
  const previousExecutableIdx = useMemo(
    () => getPreviousExecutableIdx(getContractFieldWithNews('paymentPlan'), idx),
    [getContractFieldWithNews, idx]
  );

  // Misma regla que buildChargeAccountDataSource (resolveSocialSecurityPayment):
  // con pago anticipado se usan los certificados del mes actual; en caso
  // contrario, los del periodo ejecutable anterior.
  const certSourcePayment = useMemo(
    () => resolveSocialSecurityPayment(contract, idx, getContractFieldWithNews).payment || null,
    [contract, idx, getContractFieldWithNews]
  );

  const hasCertificates = !!certSourcePayment?.certificates;

  // Primer periodo ejecutable sin pago anticipado: no existe periodo previo de
  // SS, así que el consolidado se compone con los certificados de afiliación
  // del contrato (EPS/AFP/ARL), que viven a nivel de contrato y no del pago.
  const useAffiliationCerts = previousExecutableIdx < 0 && !certSourcePayment;
  const hasAffiliationCerts = !!(
    contract.certificates?.eps && contract.certificates?.afp && contract.certificates?.arl
  );

  const handleDownloadChargeAccount = async (forceGenerate = false) => {
    handleClose();
    if (hasChargeAccount && !forceGenerate) {
      downloadChargeAccount(payment.chargeAccount);
      return;
    }

    setLoading("chargeAccount");
    const { dataSource, paymentPlan } = buildChargeAccountDataSource({
      contract, payment: paymentKey, users, getContractFieldWithNews
    });

    const saveToStorage = true;

    try {
      const response = await HandlePdfChargeAccount({ ...dataSource, paymentPlan, saveToStorage }, user);
      if (response?.blob) {
        const url = URL.createObjectURL(response.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cuenta_cobro_${contract.contractCode}_${paymentKey}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else if (response?.result && response.downloadUrl) {
        await updateContract(contract.contractCode, {
          [`payments.${paymentKey}.chargeAccount`]: response.downloadUrl,
          [`payments.${paymentKey}.chargeAccountHash`]: response.fileHash,
          [`payments.${paymentKey}.hashFileUrl`]: response.hashDownloadUrl
        }, user);
        if (setContract) {
          setContract(prev => ({
            ...prev,
            payments: {
              ...prev.payments,
              [paymentKey]: {
                ...prev.payments?.[paymentKey],
                chargeAccount: response.downloadUrl,
                hashFileUrl: response.hashDownloadUrl
              }
            }
          }));
        }
        downloadChargeAccount(response.downloadUrl);
      }
    } catch (error) {
      console.error('Error al descargar PDF:', error);
    } finally {
      setLoading(null);
    }
  };

  const handleDownloadZip = async () => {
    handleClose();
    setLoading("zip");
    try {
      const urls = [];
      if (payment.chargeAccount) {
        urls.push({ url: payment.chargeAccount, name: `cuenta_cobro_${contract.contractCode}_${paymentKey}` });
      }
      const certs = payment.certificates;
      if (certs) {
        if (certs.form) urls.push({ url: certs.form, name: `certificado_form_${contract.contractCode}_${paymentKey}` });
        if (certs.payroll) urls.push({ url: certs.payroll, name: `certificado_payroll_${contract.contractCode}_${paymentKey}` });
        if (certs.pensioner) urls.push({ url: certs.pensioner, name: `certificado_pensioner_${contract.contractCode}_${paymentKey}` });
        if (certs.transaction) urls.push({ url: certs.transaction, name: `certificado_transaction_${contract.contractCode}_${paymentKey}` });
      }
      if (payment.hashFileUrl) {
        urls.push({ url: payment.hashFileUrl, name: `firma_electronica_${contract.contractCode}_${paymentKey}` });
      }

      if (urls.length === 0) return;

      const response = await fetch(
        `https://handlepdfdownload-${process.env.REACT_APP_RUN_ID}.us-east1.run.app`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ urls, zipName: `documentos_${contract.contractCode}_${paymentKey}` }),
        }
      );

      if (!response.ok) throw new Error("Error al descargar ZIP");

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `documentos_${contract.contractCode}_${paymentKey}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar ZIP:", error);
    } finally {
      setLoading(null);
    }
  };

  const handleMergePdf = async (forceGenerate = false) => {
    handleClose();
    if (hasChargeAccountFull && !forceGenerate) {
      downloadChargeAccount(payment.chargeAccountFull);
      return;
    }

    setLoading("merge");
    try {
      const urls = [payment.chargeAccount];
      if (payment.hashFileUrl) urls.push(payment.hashFileUrl);
      if (useAffiliationCerts) {
        const affCerts = contract.certificates || {};
        if (affCerts.eps) urls.push(affCerts.eps);
        if (affCerts.afp) urls.push(affCerts.afp);
        if (affCerts.arl) urls.push(affCerts.arl);
      } else {
        const certs = certSourcePayment?.certificates;
        if (certs?.form) urls.push(certs.form);
        if (certs?.payroll) urls.push(certs.payroll);
        if (certs?.pensioner) urls.push(certs.pensioner);
        if (certs?.transaction) urls.push(certs.transaction);
      }

      const response = await fetch(
        `https://${process.env.REACT_APP_SERVER}.cloudfunctions.net/handlePdfMerge`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + user.token,
          },
          body: JSON.stringify({
            urls,
            bucket: process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
            filePath: `${contract.contractCode}/payment_${idx}_certificates`
          }),
        }
      );
      const data = await response.json();
      if (data.result && data.fileUrl) {
        await updateContract(contract.contractCode, {
          [`payments.${paymentKey}.chargeAccountFull`]: data.fileUrl
        }, user);
        if (setContract) {
          setContract(prev => ({
            ...prev,
            payments: {
              ...prev.payments,
              [paymentKey]: {
                ...prev.payments?.[paymentKey],
                chargeAccountFull: data.fileUrl
              }
            }
          }));
        }
        downloadChargeAccount(data.fileUrl);
      }
    } catch (error) {
      console.error('Error merging PDFs:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen} disabled={!!loading}>
        {loading
          ? <CircularProgress size={20} />
          : <MoreVertIcon />
        }
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleDownloadChargeAccount(false)}>
          <ListItemIcon>
            <DownloadIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {hasChargeAccount ? "Descargar cuenta de cobro" : "Generar cuenta de cobro"}
          </ListItemText>
          {hasChargeAccount && isAdmin &&
            <IconButton
              sx={{ ml: 1 }}
              size="small"
              title="Regenerar"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadChargeAccount(true);
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        </MenuItem>
        {hasChargeAccount && (hasCertificates || (useAffiliationCerts && hasAffiliationCerts)) &&
          <MenuItem onClick={() => handleMergePdf(false)}>
            <ListItemIcon>
              <MergeTypeIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {hasChargeAccountFull ? "Descargar PDF consolidado" : "Generar PDF consolidado"}
            </ListItemText>
            {hasChargeAccountFull && isAdmin &&
              <IconButton
                size="small"
                edge="end"
                title="Regenerar"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMergePdf(true);
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            }
          </MenuItem>
        }
        {hasChargeAccount &&
          <MenuItem onClick={handleDownloadZip}>
            <ListItemIcon>
              <FolderZipIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Descargar .zip de documentos
            </ListItemText>
          </MenuItem>
        }
      </Menu>
    </>
  );
};

export default ContractViewActions;
